import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { bâtimentsService } from "../../services";
import { CustomDialog, Loading, showToast } from "..";
import { Adresse, Site } from "../../interfaces";
import AdressesTable from "./AdressesTable";
import FormAdresse from "./FormAdresse";
import { AutoComplete } from "primereact/autocomplete";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../utils/UserProvider";

interface ModalAddBâtimentProps {
  modalIsOpenBatiment: boolean;
  closeModalBatiment: () => void;
  sites: Site[];
}

const ModalAddBâtiment = ({
  modalIsOpenBatiment,
  closeModalBatiment,
  sites,
}: ModalAddBâtimentProps) => {
  const queryClient = useQueryClient();
  const { client } = useAuth();
  const [show, setShow] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [adresses, setAdresses] = useState<Adresse[]>([]);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [filteredSites, setFilteredSites] = useState<Site[]>([]);
  const [monopropriete, setMonopropriete] = useState<string>("Oui");

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data: any) => {
    try {
      let bâtimentData = {};
      if (adresses.length !== 0) {
        if (selectedSite) {
          if (data.batiment.date_construction) {
            bâtimentData = {
              nom_bâtiment: data.batiment.nom_bâtiment,
              date_construction: data.batiment.date_construction,
              site_id: selectedSite.id,
              env_client_id: client.id,
              monopropriété: monopropriete,
              adresses: adresses.map((adresse) => ({
                nom_adresse: adresse.nom_adresse,
                rue: adresse.rue,
                rue2: adresse.rue2,
                code_postal: adresse.code_postal,
                ville: adresse.ville,
                pays: adresse.pays,
              })),
            };
          } else {
            bâtimentData = {
              nom_bâtiment: data.batiment.nom_bâtiment,
              env_client_id: client.id,
              site_id: selectedSite.id,
              adresses: adresses.map((adresse) => ({
                nom_adresse: adresse.nom_adresse,
                rue: adresse.rue,
                rue2: adresse.rue2,
                code_postal: adresse.code_postal,
                ville: adresse.ville,
                pays: adresse.pays,
              })),
            };
          }
          setLoading(true);
          await bâtimentsService.post(bâtimentData).then(() => {
            setLoading(false);
            showToast("success", "Bâtiment ajouté avec succéss");
            queryClient.refetchQueries({
              queryKey: ["batiments"],
            });
            setAdresses([]);
            closeModalBatiment();
            setSelectedSite(null);
            reset();
          });
        } else {
          showToast("warning", "Veuillez sélectionner votre site.");
        }
      } else {
        showToast("warning", "Veuillez ajouter au moins une adresse.");
      }
    } catch (e) {
      showToast("warning", "Vous devez sélectionner votre site.");
      console.log("Error while fetching", e);
    }
  };

  const handleAddressChange = () => {
    const formData = getValues("adresse");
    if (
      formData.nom_adresse === "" ||
      formData.code_postal === "" ||
      formData.ville === "" ||
      formData.pays === ""
    ) {
      showToast("warning", "Remplissez les champs d'adresse");
    } else {
      setAdresses([...adresses, formData]);
      reset({ adresses: {} });
      setShow(false);
    }
  };

  const onRemoveAdresse = (index) => {
    const updatedFiles = [...adresses];
    updatedFiles.splice(index, 1);
    setAdresses(updatedFiles);
  };

  const search = (event) => {
    setTimeout(() => {
      let _filteredSites;

      if (!event.query.trim().length) {
        _filteredSites = [...sites];
      } else {
        _filteredSites = sites.filter((site) => {
          return site.nom_site
            .toLowerCase()
            .includes(event.query.toLowerCase());
        });
      }

      setFilteredSites(_filteredSites);
    }, 250);
  };

  return (
    <>
      <CustomDialog
        header="Ajouter un bâtiment"
        closeModalDialog={() => {
          closeModalBatiment();
          setShow(false);
          setAdresses([]);
          setSelectedSite(null);
          reset();
        }}
        visible={modalIsOpenBatiment}
      >
        <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
          <div className="relative w-full h-full ">
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-3 sm:col-span-3">
                  <label
                    htmlFor="nom_bâtiment"
                    className="block text-md font-bold leading-6 text-gray-900"
                  >
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="nom_bâtiment"
                    id="nom_bâtiment"
                    placeholder="Saisir nom"
                    className=" my-5 border border-gray-300 text-gray-900 text-md font-semibold rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200 block w-full p-2.5 "
                    {...register("batiment.nom_bâtiment", {
                      required: true,
                      maxLength: 20,
                    })}
                  />
                  {errors.batiment && (
                    <span className="bg-red-100 text-red-800 text-md font-medium mr-2 mt-2 px-2.5 py-0.5 rounded ">
                      champs obligatoire
                    </span>
                  )}
                </div>
                <div className="col-span-3 sm:col-span-3">
                  <label
                    htmlFor="date_construction"
                    className="block text-md font-bold leading-6 text-gray-900"
                  >
                    Date de construction
                  </label>
                  <input
                    type="date"
                    name="date_construction"
                    id="date_construction"
                    placeholder="Saisir le numéro"
                    className=" my-5 border border-gray-300 text-gray-900 text-md font-semibold rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200 block w-full p-2.5 "
                    {...register("batiment.date_construction")}
                  />
                </div>


                
                <div className="col-span-3 sm:col-span-3">
                  <label
                    htmlFor="site_id"
                    className="block text-md font-bold leading-6 text-gray-900"
                  >
                    Site *
                  </label>

                  <AutoComplete
                    value={selectedSite}
                    suggestions={filteredSites}
                    field="nom_site"
                    completeMethod={search}
                    onChange={(e) => setSelectedSite(e.value)}
                    placeholder="Sélectionner un site"
                    className="h-12 my-3 border-gray-300 text-gray-900 font-semibold text-md rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200  w-full "
                    dropdown
                  />
                </div>

                <div className="col-span-3 sm:col-span-3">
                <label
                  htmlFor="monopropriete"
                  className="block text-md font-bold leading-6 text-gray-900"
                >
                  Monopropriété *
                </label>
                <select
                  id="monopropriete"
                  name="monopropriete"
                  value={monopropriete}
                  onChange={(e) => setMonopropriete(e.target.value)}
                  className="h-12 my-3 border-gray-300 text-gray-900 font-semibold text-md rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200  w-full "
                >
                  <option value="Oui">Oui</option>
                  <option value="Non">Non</option>
                </select>
              </div>




              </div>

              <div className="flex space-x-40 col-span-3 ">
                <span className="block text-lg font-bold leading-6 text-gray-900">
                  Adresse(s)
                </span>
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="inline-flex items-center justify-center px-2 py-2 text-sm font-medium text-white bg-red-800 rounded-full"
                >
                  <img
                    src="images/plus.png"
                    className="inline-block"
                    alt="plus"
                  />
                </button>
              </div>
              {show && (
                <FormAdresse
                  register={register}
                  handleAddressChange={handleAddressChange}
                />
              )}
              <div className="relative  overflow-y-auto ">
                <AdressesTable
                  adresses={adresses}
                  onRemoveAdresse={onRemoveAdresse}
                />
              </div>
              <p className=" text-lg font-thin my-4">* obligatoire</p>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="text-white mt-5  bg-gexpertise-200 hover:bg-gexpertise-100 font-medium rounded-lg text-md px-6 py-3  text-center "
                >
                  {loading && (
                    <svg
                      aria-hidden="true"
                      role="status"
                      className="inline w-4 h-4 mr-3 text-white animate-spin"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="#E5E7EB"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        </form>
      </CustomDialog>
      {loading && <Loading />}
    </>
  );
};

export default ModalAddBâtiment;
